import React, { useEffect, useState } from "react";
import { AlertTriangle, Check, Loader2, ShieldCheck, Sparkles } from "lucide-react";

import {
  fetchAuthenticatedPrivateMediaBlob,
  generateVisualDirectionOwnerQA,
  getLatestVisualDirectionOwnerQA,
  getVisualDirectionProviderPreflight,
} from "../../lib/brandStudioApi";

export default function VisualDirectionProviderAdapterOwnerPanelV1() {
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [generationStatus, setGenerationStatus] = useState("idle");
  const [generationError, setGenerationError] = useState("");
  const [result, setResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewStatus, setPreviewStatus] = useState("idle");

  useEffect(() => {
    let active = true;

    const boot = async () => {
      setStatus("loading");
      setError("");

      try {
        const preflight = await getVisualDirectionProviderPreflight();
        if (!active) return;
        setData(preflight);
        setStatus("ready");
      } catch (problem) {
        if (!active) return;
        const detail = problem?.response?.data?.detail;
        const code = typeof detail === "object" && detail ? detail.code : typeof detail === "string" ? detail : "";
        setError(code || "Не удалось собрать VD3 provider preflight.");
        setStatus("error");
      }

      try {
        const latest = await getLatestVisualDirectionOwnerQA();
        if (!active || !latest) return;
        setResult(latest);
        setGenerationStatus("success");
      } catch {
        // Latest preview is optional; preflight remains available.
      }
    };

    boot();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const imageUrl = result?.image_url;
    if (!imageUrl) {
      setPreviewUrl("");
      setPreviewStatus("idle");
      return undefined;
    }

    let cancelled = false;
    let createdObjectUrl = null;
    setPreviewUrl("");
    setPreviewStatus("loading");

    fetchAuthenticatedPrivateMediaBlob(imageUrl)
      .then((blob) => {
        if (cancelled) return;
        createdObjectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(createdObjectUrl);
          createdObjectUrl = null;
          return;
        }
        setPreviewUrl(createdObjectUrl);
        setPreviewStatus("decoding");
      })
      .catch((problem) => {
        if (cancelled) return;
        setPreviewStatus("error");
        const detail = problem?.response?.data?.detail;
        const code = typeof detail === "object" && detail ? detail.code : typeof detail === "string" ? detail : "";
        setGenerationError(code || "Не удалось загрузить private preview.");
      });

    return () => {
      cancelled = true;
      if (createdObjectUrl) URL.revokeObjectURL(createdObjectUrl);
    };
  }, [result?.image_url]);

  const money = (micros) => `${(Number(micros || 0) / 1_000_000).toFixed(4)}`;

  const generateOne = async () => {
    setGenerationStatus("running");
    setGenerationError("");
    setResult(null);
    try {
      const requestId = window.crypto.randomUUID();
      const value = await generateVisualDirectionOwnerQA(requestId);
      setResult(value);
      setGenerationStatus("success");
    } catch (problem) {
      const detail = problem?.response?.data?.detail;
      const code = typeof detail === "object" && detail ? detail.code : typeof detail === "string" ? detail : "";
      setGenerationError(code || "Live generation завершилась ошибкой.");
      setGenerationStatus("error");
    }
  };

  return (
    <section className="rounded-[24px] border border-dashed border-[#7A684F] bg-[#FFF9F0] p-5 md:p-7" data-testid="visual-direction-provider-adapter-owner-panel-v1">
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">OWNER QA · VD3.2</div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">Visual Direction Provider Adapter</h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Preflight остаётся zero-cost. Платный Prodia вызов возможен только по отдельному нажатию Owner ниже. Каждое нажатие создаёт новый exploration visual; повторять можно по необходимости.
      </p>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#796652]">
          <Loader2 size={14} className="animate-spin" /> Проверяю provider adapter…
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] leading-5 text-[#6B443A]">{error}</div>
      )}

      {status === "ready" && data && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              {data.adapter_ready ? <Check size={12} /> : <ShieldCheck size={12} />}
              {data.adapter_ready ? "Adapter technically ready" : "Adapter needs configuration"}
            </span>
            <span className="text-[10.5px] text-[#8A745E]">{data.provider} · {data.model}</span>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Routing</div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                provider route: {data.route_allowed ? "allowed" : "blocked"}<br />
                token: {data.token_configured ? "configured" : "missing"}<br />
                VD2 contract: {data.generation_contract_ready ? "ready" : "not ready"}
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Provider payload</div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                references: {data.payload.references?.length || 0}<br />
                patterns: {data.payload.selected_patterns?.length || 0}<br />
                explicit decisions: {data.payload.explicit_decision_values?.length || 0}
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Current price</div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                {data.pricing.reference_count} input images<br />
                768 × 1024<br />
                expected: {money(data.pricing.expected_cost_micros_per_image)} / image<br />
                hard cap: {money(data.pricing.hard_cap_micros_per_image)} / image
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Live-call safety</div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                live endpoint: {data.live_endpoint_exposed ? "exposed" : "NOT exposed"}<br />
                privacy: {data.privacy.status}<br />
                decision: {data.privacy.decision || "—"}<br />
                paid call allowed: {data.privacy.live_call_allowed ? "YES" : "NO"}
              </div>
            </article>
          </div>

          <div className="mt-4 rounded-[14px] border border-[#DED0BC] bg-white p-4">
            <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Exactly what may leave our server</div>
            <div className="mt-2 text-[11px] leading-5 text-[#655141]">
              business context fields: {(data.payload.business_context_fields || []).join(", ") || "—"}<br />
              explicit visual decisions: yes · semantic pattern labels: yes · selected reference image binaries: yes<br />
              exact brand name: NO · exact logo: NO · exact font: NO · contact data: NO · raw atoms: NO<br />
              reference notes: NO · reference signals: NO
            </div>
          </div>

          <div className="mt-4 rounded-[14px] border border-[#E0C4A6] bg-[#FFF4E8] p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-[#8A6D53]">
              <AlertTriangle size={13} /> Privacy checkpoint
            </div>
            <div className="mt-2 text-[11.5px] leading-5 text-[#655141]">
              Privacy review завершён, риск раскрыт Owner. Публичные материалы не подтверждают retention для synchronous inputs,
              exclusion from training и deletion window. {data.privacy.decision === "owner_risk_accepted"
                ? "Owner принял этот раскрытый риск для Owner QA; это не меняет будущие customer defaults и не запускает генерацию автоматически."
                : "Решение Owner ещё не принято; live generation остаётся выключенной."}
            </div>
          </div>

          {!!data.blocking_reasons?.length && (
            <div className="mt-4 rounded-[14px] border border-[#DED0BC] bg-[#FFFCF6] p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Blocking reasons</div>
              <div className="mt-2 space-y-1 text-[11px] leading-5 text-[#655141]">
                {data.blocking_reasons.map((item) => <div key={item}>• {item}</div>)}
              </div>
            </div>
          )}

          <div className="mt-5 rounded-[16px] border border-[#CDBB9F] bg-white p-4">
            <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Owner live generation</div>
            <p className="mt-2 text-[11.5px] leading-5 text-[#655141]">
              Каждое нажатие запускает отдельный Prodia request и создаёт новый Visual Direction exploration visual. Это QA-действие Owner, не лимит пользовательского продукта.
            </p>
            <button
              type="button"
              onClick={generateOne}
              disabled={!data.privacy.live_call_allowed || !data.adapter_ready || generationStatus === "running"}
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#8A6D53] px-4 py-2 text-[11px] text-[#4B3829] disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="vd3-owner-generate-one"
            >
              {generationStatus === "running" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {generationStatus === "running" ? "Генерирую…" : "Создать Visual Direction"}
            </button>

            {generationStatus === "error" && (
              <div className="mt-3 text-[11.5px] leading-5 text-[#874F45]">{generationError}</div>
            )}

            {generationStatus === "success" && result && (
              <div className="mt-4">
                {(previewStatus === "loading" || previewStatus === "decoding") && (
                  <div className="relative flex min-h-[220px] items-center justify-center overflow-hidden rounded-[14px] border border-[#DED0BC] bg-[#F5F0E8] text-[11px] text-[#796652]">
                    <Loader2 size={14} className="mr-2 animate-spin" /> Загружаю private preview…
                    {previewStatus === "decoding" && previewUrl && (
                      <img
                        src={previewUrl}
                        alt=""
                        aria-hidden="true"
                        className="pointer-events-none absolute h-px w-px opacity-0"
                        onLoad={() => setPreviewStatus("ready")}
                        onError={() => {
                          setPreviewStatus("error");
                          setGenerationError("Private preview получен, но браузер не смог декодировать изображение.");
                        }}
                        data-testid="vd3-owner-private-preview-probe"
                      />
                    )}
                  </div>
                )}
                {previewStatus === "ready" && previewUrl && (
                  <img
                    src={previewUrl}
                    alt="Visual Direction Owner QA"
                    className="max-h-[720px] w-full rounded-[14px] border border-[#DED0BC] bg-[#F5F0E8] object-contain"
                    onError={() => {
                      setPreviewStatus("error");
                      setGenerationError("Private preview получен, но браузер не смог отобразить изображение.");
                    }}
                    data-testid="vd3-owner-private-preview"
                  />
                )}
                {previewStatus === "error" && (
                  <div className="rounded-[14px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[11.5px] text-[#874F45]">
                    Private preview не загрузился. Генерация сохранена; повторный Prodia-вызов не нужен.
                  </div>
                )}
                <div className="mt-2 text-[11px] leading-5 text-[#655141]">
                  actual cost: {money(result.actual_cost_micros)} · references: {result.reference_ids?.length || 0} · reused: {result.reused ? "yes" : "no"}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
